"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const css_element_queries_1 = require("css-element-queries");
const constants_1 = require("playable/dist/src/constants");
const player_api_decorator_1 = tslib_1.__importDefault(require("playable/dist/src/core/player-api-decorator"));
const lodash_1 = require("lodash");
const cards_container_1 = tslib_1.__importDefault(require("./cards-container/cards-container"));
const cards_manager_1 = tslib_1.__importDefault(require("./cards-manager/cards-manager"));
const cards_config_1 = tslib_1.__importDefault(require("./cards-config/cards-config"));
const constants_2 = require("./constants");
const dom_update_delay_1 = require("./utils/dom-update-delay");
class CardsModule {
    constructor({ eventEmitter, rootContainer, engine }) {
        this.eventEmitter = eventEmitter;
        this.rootContainer = rootContainer;
        this.engine = engine;
        this.cardsConfig = new cards_config_1.default();
        this.isInitialized = false;
        this.hide = lodash_1.noop;
        this.show = lodash_1.noop;
        this.handlePlayerSizeChange = this.handlePlayerSizeChange.bind(this);
    }
    // Lazy initialization of cards-related stuff
    initialize() {
        if (this.isInitialized) {
            return;
        }
        this.initContainer();
        this.initManager();
        this.initEventListeners();
        this.hide = () => this.cardsContainer.hide();
        this.show = () => this.cardsContainer.show();
        this.isInitialized = true;
        setTimeout(() => this.handlePlayerStateChange({
            nextState: this.engine.getCurrentState(),
        }), 0);
    }
    initContainer() {
        this.cardsContainer = new cards_container_1.default({ cardsConfig: this.cardsConfig });
        this.rootContainer.appendComponentElement(this.cardsContainer.node);
    }
    initManager() {
        this.cardsManager = new cards_manager_1.default({
            cardsContainer: this.cardsContainer,
            eventEmitter: this.eventEmitter,
            cardsConfig: this.cardsConfig,
            engine: this.engine,
        });
    }
    initEventListeners() {
        this.eventEmitter.on(constants_1.VIDEO_EVENTS.STATE_CHANGED, this.handlePlayerStateChange, this);
        this.eventEmitter.on(constants_1.UI_EVENTS.MAIN_BLOCK_HIDE, this.cardsContainer.onControlsHidden, this.cardsContainer);
        this.eventEmitter.on(constants_1.UI_EVENTS.MAIN_BLOCK_SHOW, this.cardsContainer.onControlsShown, this.cardsContainer);
        // wait for playerContainerNode inserted to DOM and get size for case if it's not added yet
        (0, dom_update_delay_1.waitForDomUpdate)().then(() => (this.resizeSensor = new css_element_queries_1.ResizeSensor(this.rootContainer.getElement(), this.handlePlayerSizeChange)));
        this.eventEmitter.on(constants_2.EVENTS.CARD_CLOSED, this.cardsManager.handleCardClose, this.cardsManager);
    }
    addCard(cardData) {
        this.initialize();
        return this.cardsManager.addCard(cardData);
    }
    addCards(cardsData) {
        this.initialize();
        return this.cardsManager.addCards(cardsData);
    }
    removeCard(id) {
        this.cardsManager.removeCard(id);
    }
    updateCards(cardsData) {
        return this.cardsManager.updateCards(cardsData);
    }
    clearCards() {
        if (!this.isInitialized) {
            return;
        }
        this.cardsManager.clearCards();
    }
    setAnchorPoint(anchorPoint) {
        if (this.cardsConfig.anchorPoint === anchorPoint) {
            return;
        }
        this.cardsConfig.anchorPoint = anchorPoint;
    }
    setOrientation(orientation) {
        if (this.cardsConfig.orientation === orientation) {
            return;
        }
        this.cardsConfig.orientation = orientation;
    }
    onCardClose(callback) {
        this.eventEmitter.on(constants_2.EVENTS.CARD_CLOSED, callback);
    }
    onCardShown(callback) {
        this.eventEmitter.on(constants_2.EVENTS.CARD_SHOWN, callback);
    }
    onCardHidden(callback) {
        this.eventEmitter.on(constants_2.EVENTS.CARD_HIDDEN, callback);
    }
    setCardsClosable(isClosable) {
        const isPreviewMode = !isClosable;
        if (this.cardsConfig.isPreviewMode === isPreviewMode) {
            return;
        }
        this.cardsConfig.isPreviewMode = isPreviewMode;
    }
    setActiveCard(id) {
        if (this.isInitialized) {
            return this.cardsManager.showSelectedCard(id);
        }
    }
    hideCardsContainer() {
        this.hide();
    }
    showCardsContainer() {
        this.show();
    }
    handlePlayerSizeChange() {
        this.cardsManager.handlePlayerSizeChange();
    }
    handlePlayerStateChange({ nextState }) {
        switch (nextState) {
            case constants_1.EngineState.PLAYING:
                this.cardsManager.handleVideoPlayStart();
                break;
            case constants_1.EngineState.PAUSED:
                this.cardsManager.handleVideoPlayPause();
                break;
            case constants_1.EngineState.SEEK_IN_PROGRESS:
                this.cardsManager.handleSeekPositionChange();
                break;
            default:
                break;
        }
    }
    unbindEvents() {
        this.eventEmitter.off(constants_1.VIDEO_EVENTS.STATE_CHANGED, this.handlePlayerStateChange, this);
        this.eventEmitter.off(constants_1.UI_EVENTS.MAIN_BLOCK_HIDE, this.cardsContainer.onControlsHidden, this.cardsContainer);
        this.eventEmitter.off(constants_1.UI_EVENTS.MAIN_BLOCK_SHOW, this.cardsContainer.onControlsShown, this.cardsContainer);
        this.eventEmitter.off(constants_2.EVENTS.CARD_CLOSED);
        this.eventEmitter.off(constants_2.EVENTS.CARD_SHOWN);
        this.eventEmitter.off(constants_2.EVENTS.CARD_HIDDEN);
    }
    destroy() {
        if (!this.isInitialized) {
            return;
        }
        this.unbindEvents();
        this.cardsContainer.destroy();
        this.cardsManager.destroy();
        this.cardsConfig.destroy();
        if (this.resizeSensor) {
            this.resizeSensor.detach(this.handlePlayerSizeChange);
        }
        delete this.cardsContainer;
        delete this.cardsManager;
        delete this.cardsConfig;
    }
}
CardsModule.dependencies = ['eventEmitter', 'rootContainer', 'engine'];
tslib_1.__decorate([
    (0, player_api_decorator_1.default)()
], CardsModule.prototype, "addCard", null);
tslib_1.__decorate([
    (0, player_api_decorator_1.default)()
], CardsModule.prototype, "addCards", null);
tslib_1.__decorate([
    (0, player_api_decorator_1.default)()
], CardsModule.prototype, "removeCard", null);
tslib_1.__decorate([
    (0, player_api_decorator_1.default)()
], CardsModule.prototype, "updateCards", null);
tslib_1.__decorate([
    (0, player_api_decorator_1.default)()
], CardsModule.prototype, "clearCards", null);
tslib_1.__decorate([
    (0, player_api_decorator_1.default)()
], CardsModule.prototype, "setAnchorPoint", null);
tslib_1.__decorate([
    (0, player_api_decorator_1.default)()
], CardsModule.prototype, "setOrientation", null);
tslib_1.__decorate([
    (0, player_api_decorator_1.default)()
], CardsModule.prototype, "onCardClose", null);
tslib_1.__decorate([
    (0, player_api_decorator_1.default)()
], CardsModule.prototype, "onCardShown", null);
tslib_1.__decorate([
    (0, player_api_decorator_1.default)()
], CardsModule.prototype, "onCardHidden", null);
tslib_1.__decorate([
    (0, player_api_decorator_1.default)()
], CardsModule.prototype, "setCardsClosable", null);
tslib_1.__decorate([
    (0, player_api_decorator_1.default)()
], CardsModule.prototype, "setActiveCard", null);
tslib_1.__decorate([
    (0, player_api_decorator_1.default)()
], CardsModule.prototype, "hideCardsContainer", null);
tslib_1.__decorate([
    (0, player_api_decorator_1.default)()
], CardsModule.prototype, "showCardsContainer", null);
exports.default = CardsModule;
//# sourceMappingURL=cards-module.js.map